import { useState } from "react";

import { ChevronDownIcon, ChevronUpIcon, PlayIcon } from "lucide-react";

import { Button } from "@components/Button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@components/Collapsible";
import { Image } from "@components/Image";
import { Link } from "@components/Link";
import { Stack } from "@components/Stack";
import { Tooltip, TooltipContent, TooltipTrigger } from "@components/Tooltip";
import { Typography } from "@components/Typography";

import { EToySessionState } from "@api/toys";

import { cn } from "@lib/utils";

import { SKELETON_BLOCK_CLASS } from "@configs/constants";
import { ENV_CONFIG } from "@configs/env";
import { ICON_PATHS } from "@configs/icons";
import {
	TOYS_CONTROL_ENDED_HINT,
	TOYS_CONTROL_ENDED_MESSAGE,
	TOYS_OFFLINE_HINT,
	TOYS_OFFLINE_MESSAGE,
	deviceTextStatusMap,
} from "@configs/toys";

import { ToyActuatorControls } from "./ToyActuatorControls";
import { useToysControls } from "./useToysControls";

interface ToysCompactViewProps {
	extraCompact?: boolean;
}

const ToyGuideTooltip = ({ extraCompact = false }: ToysCompactViewProps) => {
	const [isOpen, setIsOpen] = useState(false);

	const handleToggle = () => setIsOpen((open) => !open);

	return (
		<Tooltip open={isOpen} onOpenChange={setIsOpen}>
			<TooltipTrigger
				type="button"
				aria-label="How to view toy instructions"
				closeOnClick={false}
				onClick={handleToggle}
				className={cn(
					`flex size-6 shrink-0 cursor-pointer items-center justify-center rounded-sm outline-none focus-visible:ring-2
					focus-visible:ring-primary`,
					extraCompact && "size-4",
				)}
			>
				<Image
					src={ICON_PATHS.QUESTION}
					width={extraCompact ? 16 : 24}
					height={extraCompact ? 16 : 24}
					alt=""
					className={extraCompact ? "size-4" : "size-6"}
				/>
			</TooltipTrigger>
			<TooltipContent side="top" sideOffset={8} zIndexMax className="max-w-52">
				<Typography size="sm">
					Enlarge your browser window to view the how-to instructions, or visit ModelHub{" "}
					<Link
						href={`${ENV_CONFIG.BASE_HUB_URL}instruction/interactive_toys`}
						target="_blank"
						rel="noopener noreferrer"
						role="link"
						underline="none"
						aria-disabled="true"
						className="text-primary"
					>
						here
					</Link>
				</Typography>
			</TooltipContent>
		</Tooltip>
	);
};

export const ToysCompactView = ({ extraCompact = false }: ToysCompactViewProps) => {
	const {
		isOpen,
		isOpenToyContainer,
		providerName,
		session,
		capabilities,
		sessionToken,
		isLoading,
		isError,
		sessionState,
		isToyContainerDisabled,
		isActionPending,
		handleOpenPlay,
		handleStop,
		handleOpenChange,
	} = useToysControls();

	if (!isOpenToyContainer) {
		return null;
	}

	const isRunning = sessionState === EToySessionState.GRANTED_RUNNING;
	const canPlay = sessionState === EToySessionState.GRANTED_STOPPED;
	const isOffline = sessionState === EToySessionState.SUSPENDED_OFFLINE;
	const isUnavailable = sessionState === EToySessionState.CLOSED;
	const isControlEnded =
		!isLoading && (sessionState === EToySessionState.IDLE || sessionState === EToySessionState.PENDING);
	const showStatusMessage = !isLoading && (isControlEnded || isOffline);
	const statusMessage = isOffline ? TOYS_OFFLINE_MESSAGE : TOYS_CONTROL_ENDED_MESSAGE;
	const statusHint = isOffline ? TOYS_OFFLINE_HINT : TOYS_CONTROL_ENDED_HINT;
	const isInlineControlsHidden = isOpen && !showStatusMessage;
	const actionLabel = isRunning
		? "Pause"
		: canPlay
			? "Start"
			: isOffline
				? "Connection lost"
				: isUnavailable
					? "Service unavailable"
					: "Control ended";

	const renderControls = (layout: "compact" | "inline") => {
		if (isLoading) {
			return (
				<div
					className={cn("flex w-full min-w-0 gap-3.5", layout === "compact" && "min-h-25 items-center")}
					aria-hidden="true"
				>
					<div className={cn(SKELETON_BLOCK_CLASS, "h-8 flex-1 rounded-full")} />
					<div
						className={cn(SKELETON_BLOCK_CLASS, "h-8 flex-1 rounded-full", layout === "compact" && "h-20 rounded-lg")}
					/>
				</div>
			);
		}

		if (isError || !session || !capabilities || !sessionToken) {
			return (
				<Typography role="status" size="xs" color="grey-tertiary">
					Unable to load toy controls. Please try again later.
				</Typography>
			);
		}

		return (
			<div
				inert={isToyContainerDisabled}
				aria-disabled={isToyContainerDisabled}
				className={cn("flex min-w-0 flex-1 flex-col gap-3.5", isToyContainerDisabled && "opacity-30")}
			>
				{capabilities.capabilities.actuators.map((actuator) => (
					<ToyActuatorControls
						key={`${session.session_id}:${sessionToken}:${actuator.id}`}
						actuator={actuator}
						commands={session.motion?.actuators}
						disabled={isToyContainerDisabled}
						presets={capabilities.presets}
						sessionToken={sessionToken}
						layout={layout}
					/>
				))}
			</div>
		);
	};

	const actionButton = isLoading ? (
		<div className={cn(SKELETON_BLOCK_CLASS, extraCompact ? "h-5 w-20.25 rounded-full" : "h-8 w-18 rounded-full")} />
	) : showStatusMessage ? (
		<div
			className={cn(
				"flex h-8 w-18 items-center justify-center gap-1.5 px-0.75",
				extraCompact && "h-5 w-28 pr-2.75 pl-2",
				isOffline && "justify-start",
				isOffline && extraCompact && "justify-center px-0.75",
			)}
		>
			<Image
				src={isOffline ? ICON_PATHS.PROVIDER_IS_OFFLINE_16 : ICON_PATHS.PROVIDER_IS_DISABLED_16}
				width={extraCompact && isControlEnded ? 10 : 16}
				height={extraCompact && isControlEnded ? 10 : 16}
				alt=""
				className={cn(
					"size-4 shrink-0",
					isControlEnded && "-scale-y-100 rotate-180",
					extraCompact && isControlEnded && "size-2.5",
				)}
			/>
			<Typography type="span" color="secondary" weight="bold" className="text-[8px] leading-2.5">
				{isOffline ? "Connection" : "Control"}
				{extraCompact ? " " : <br />}
				{isOffline ? "Lost" : "Ended"}
			</Typography>
		</div>
	) : (
		<Button
			type="block"
			size="auto"
			shape="round"
			aria-label={`${actionLabel} ${providerName}`}
			disable={isActionPending || (!isRunning && !canPlay)}
			onClick={isRunning ? handleStop : handleOpenPlay}
			className={cn(
				`h-8 w-18 gap-1.5 border border-secondary bg-secondary/30 px-2 py-0 focus-visible:ring-2
					focus-visible:ring-primary`,
				canPlay && !extraCompact && "w-21.5",
				extraCompact && "h-5 w-20.25",
			)}
		>
			<Stack direction="row" align="center" spacing={6}>
				{isRunning ? (
					<span className="size-2.5 shrink-0 rounded-xs bg-red-100" aria-hidden="true" />
				) : canPlay ? (
					<PlayIcon className="size-3.5 shrink-0 text-primary" fill="currentColor" strokeWidth={0} aria-hidden="true" />
				) : (
					<Image src={ICON_PATHS.PROVIDER_IS_DISABLED} width={16} height={16} alt="" className="size-4" />
				)}
				<Typography
					type="span"
					weight="bold"
					className={cn("text-[10px] leading-3", !isRunning && !canPlay && "text-[8px] leading-2.5")}
				>
					{actionLabel}
				</Typography>
			</Stack>
		</Button>
	);

	if (extraCompact) {
		return (
			<section
				aria-label={`${providerName} controls`}
				aria-busy={isLoading}
				className={`mb-1.5 flex h-7.5 w-full min-w-0 items-center justify-between gap-1 rounded-2xl bg-block pr-1.5 pl-5
					text-white`}
			>
				<div className="flex shrink-0 items-center gap-1">
					<Image
						src={ICON_PATHS.HANDY_LOGO_DESKTOP}
						width={80}
						height={20}
						alt={`The ${providerName}`}
						className="h-5 w-20"
					/>
					<ToyGuideTooltip extraCompact />
				</div>
				<Typography
					size="xs"
					color="grey-tertiary"
					align="center"
					className={cn(
						"min-w-0 flex-1 text-[9px] leading-2.5",
						isControlEnded && "truncate",
						isOffline && "line-clamp-2",
					)}
					title={
						showStatusMessage
							? `${statusMessage} ${statusHint}`
							: "Increase your browser window height to use the interactive toy controls."
					}
				>
					{showStatusMessage ? (
						`${statusMessage} ${statusHint}`
					) : (
						<>
							<span className="tablet:hidden">Increase window height</span>
							<span className="hidden tablet:inline">
								Increase your browser window height to use the interactive toy controls.
							</span>
						</>
					)}
				</Typography>
				<Typography type="span" role="status" className="sr-only">
					{isLoading ? "Loading toy controls" : deviceTextStatusMap(sessionState, session?.last_stop)}
				</Typography>
				<div className="shrink-0">{actionButton}</div>
			</section>
		);
	}

	return (
		<Collapsible
			open={isOpen}
			onOpenChange={handleOpenChange}
			aria-busy={isLoading}
			className="mb-1.5 w-full min-w-0 rounded-2xl bg-block px-3.75 py-2.5 text-white"
		>
			<Typography type="span" role="status" className="sr-only">
				{isLoading ? "Loading toy controls" : deviceTextStatusMap(sessionState, session?.last_stop)}
			</Typography>
			<header
				className={cn(
					"grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-2 tablet:gap-x-3.5",
					canPlay && "gap-x-1",
				)}
			>
				<div className="col-start-1 row-start-1 flex h-8 shrink-0 items-center gap-2.5">
					<CollapsibleTrigger
						aria-label={isOpen ? `Collapse ${providerName} controls` : `Expand ${providerName} controls`}
						className={`flex size-6 cursor-pointer items-center justify-center rounded-sm outline-none
							focus-visible:ring-2 focus-visible:ring-primary`}
					>
						{isOpen ? (
							<ChevronUpIcon className="size-4" aria-hidden="true" />
						) : (
							<ChevronDownIcon className="size-4" aria-hidden="true" />
						)}
					</CollapsibleTrigger>
					<div className="flex w-32.25 items-center justify-between">
						<Image
							src={ICON_PATHS.HANDY_LOGO_DESKTOP}
							width={100}
							height={25}
							alt={`The ${providerName}`}
							className="h-6.25 w-25"
						/>
						<ToyGuideTooltip />
					</div>
				</div>
				<div
					inert={isInlineControlsHidden}
					aria-hidden={isInlineControlsHidden}
					className={cn(
						`col-span-3 row-start-2 min-w-0 overflow-hidden transition-[height,opacity] duration-300 ease-in-out
						motion-reduce:transition-none tablet:col-span-1 tablet:col-start-2 tablet:row-start-1 tablet:h-8`,
						isInlineControlsHidden ? "h-0 opacity-0" : "h-11.5 opacity-100",
					)}
				>
					<div className="pt-3.5 tablet:pt-0">
						{showStatusMessage ? (
							<Typography
								size="xs"
								color="grey-tertiary"
								align={isOffline ? "left" : "center"}
								className="flex flex-col justify-center text-[10px] leading-3 tablet:h-8"
							>
								<span>{statusMessage}</span>
								<span>{statusHint}</span>
							</Typography>
						) : (
							renderControls("inline")
						)}
					</div>
				</div>
				<div className="col-start-3 row-start-1 shrink-0">{actionButton}</div>
			</header>
			<CollapsibleContent
				className={`duration-300 ease-in-out data-ending-style:duration-300 data-ending-style:ease-in-out
					motion-reduce:transition-none`}
			>
				<div className="pt-3.5">
					<div className="flex min-h-25 items-start gap-3.5">
						<div className={cn("relative h-25 w-20 shrink-0 tablet:w-25", isToyContainerDisabled && "opacity-30")}>
							<Image
								src={ICON_PATHS.HANDY_DEVICE_IMAGE}
								width={114.5}
								height={114.5}
								alt={`The ${providerName} device`}
								className={`size-28.625 absolute top-1/2 left-1/2 max-w-none -translate-x-1/2 -translate-y-1/2
									object-contain`}
							/>
						</div>
						{renderControls("compact")}
					</div>
				</div>
			</CollapsibleContent>
		</Collapsible>
	);
};
